import MatterRow from "./matter";
import { Input } from "./ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "./ui/command"
import axios from "axios";
import React, { KeyboardEvent, useRef, useState } from "react";
import {Matter, Contact, MatterContactLink} from "src/lib/conflix";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const apiUrl = process.env.REACT_APP_API_URL;

function NewMatter(
  props: {
    idToken: string,
    nextRefNum?: string,
    onLoading: (isLoading: boolean) => void,
    onError: (err: string) => void;
    onUpdate: () => Promise<void>;
    matterContacts: MatterContactLink[] | null;
  }) {
  const [loading, setLoading] = useState<boolean>(false);
  const [matterInfo, setMatterInfo] = useState<Matter>({
    matterId: "",
    referenceNumber: props.nextRefNum || "Auto Generate", // Empty string if no reference number is provided
    name: "",
    createdAt: ""
  });
  const [contactInfo, setContactInfo] = useState<Contact>({
    contactId: "",
    firstName: "",
    lastName: "",
    createdAt: "",
    createdBy: "",
    companyName: "",
    middleName: "",
    dateOfBirth: ""
  });
  const [designation, setDesignation] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [noMatter, setNoMatter] = useState<boolean>(false);
  const [noContact, setNoContact] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const commandInputRef = useRef<HTMLInputElement>(null);

  const matters = React.useMemo(() => {
    if (!props.matterContacts) return null;
    const seen = new Set<string>();
    const unique: Matter[] = [];
    for (const link of props.matterContacts) {
      if (!seen.has(link.matter.matterId)) {
        seen.add(link.matter.matterId);
        unique.push(link.matter);
      }
    }
    return unique.sort((a, b) => a.referenceNumber.localeCompare(b.referenceNumber));
  }, [props.matterContacts]);

  async function createMatter(name : string, referenceNumber : string) : Promise<string> {
    try {
      const response = await axios.post(
        `${apiUrl}matters`,
        { name, referenceNumber },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": props.idToken
          },
        }
      );  
        const matterId = response.data.matterId;
        if (matterId) {
          return matterId;
        }
        props.onError("No matter ID returned")
        throw new Error("No matter id returned");
    } catch (error : any) {
      if (error.message) {
        let errMessage = error.message;
        if (error.details) {
          errMessage = `${errMessage}: ${error.details}`;
        }
        props.onError(errMessage);
        console.log(error);
      } else {
        console.error("Error creating matter:", error);
        throw error;
      }
      return new Promise<string>(() => "");
    }
  }

  async function createContact(firstName : string, lastName : string, middleName : string, companyName : string, dateOfBirth : string) : Promise<string> {
    let data = {}
    if (dateOfBirth) {
      data = { firstName, lastName, middleName, companyName, dateOfBirth }
    } else {
      data = { firstName, lastName, middleName, companyName }
    }
    try {
      const response = await axios.post(
        `${apiUrl}contacts`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": props.idToken
          },
        }
      );
      const contactId = response.data.contactId;
      if (contactId) {
        return contactId;
      }
      props.onError("No contact ID returned");
      throw new Error("No contact ID returned");
    } catch (error: any) {
      if (error.message) {
        let errMessage = error.message;
        if (error.details) {
          errMessage = `${errMessage}: ${error.details}`;
        }
        props.onError(errMessage);
        console.log(error);
      } else {
        console.error("Error creating contact:", error);
        throw error;
      }
      return new Promise<string>(() => "");
    }
  }

  async function createContactInfo(contactId : string, type : string, value : string) : Promise<string> {
    try {
      const response = await axios.post(
        `${apiUrl}contacts/${contactId}/info`,
        { type, value },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": props.idToken
          },
        }
      );
      const contactInfoId = response.data.id;
      if (contactInfoId) {
        return contactInfoId;
      }
      props.onError("No contact info ID returned")
      throw new Error("No contact info id returned");
    } catch (error: any) {
      if (error.message) {
        let errMessage = error.message;
        if (error.details) {
          errMessage = `${errMessage}: ${error.details}`;
        }
        props.onError(errMessage);
        console.log(error);
      } else {
        console.error("Error creating contact info:", error);
        throw error;
      }
      return new Promise<string>(() => "");
    }
  }

  async function createMatterContactLink(matterId : string, contactId : string, primaryContactInfo: {email: string, phone: string, address: string}) : Promise<string> {
    try {
      const response = await axios.post(
        `${apiUrl}matters/${matterId}/contacts/${contactId}`,
        { 
          primaryContactInfo,
          designation
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": props.idToken
          },
        }
      );
      const data = response.data;
      if (data) {
        return data;
      }
      props.onError("No matter-contact link data returned")
      throw new Error("No matter-contact link data returned");
    } catch (error: any) {
      if (error.message) {
        let errMessage = error.message;
        if (error.details) {
          errMessage = `${errMessage}: ${error.details}`;
        }
        props.onError(errMessage);
        console.log(error);
        return new Promise<string>(() => "error");
      } else {
        console.error("Error creating matter-contact link:", error);
        throw error;
      }
    }
  }

  const submitNewRow = async (): Promise<boolean> => {
    if (!selectedMatter && !matterInfo.name) {
      setNoMatter(true);
      props.onError("A matter must have a title.")
      return false;
    }
    setNoMatter(false);
    if ((!contactInfo.firstName || !contactInfo.lastName) && !contactInfo.companyName) {
      setNoContact(true);
      props.onError("A contact must have a first and last name or a company name.")
      return false;
    }
    setNoContact(false);
    props.onError("");
    setLoading(true);
    props.onLoading(true);
    const matterId = selectedMatter ? selectedMatter.matterId : await createMatter(matterInfo.name, matterInfo.referenceNumber == "Auto Generate" ? "" : matterInfo.referenceNumber);
    const dobIso = contactInfo.dateOfBirth ? (() => { const [y, m, d] = contactInfo.dateOfBirth.split('-').map(Number); return new Date(y, m - 1, d).toISOString(); })() : "";
    const contactId = await createContact(contactInfo.firstName, contactInfo.lastName, contactInfo.middleName, contactInfo.companyName, dobIso);
    const phoneId = phone ? await createContactInfo(contactId, "phone", phone) : "";
    const emailId = email ? await createContactInfo(contactId, "email", email) : "";
    const addressId = address ? await createContactInfo(contactId, "address", address) : "";
    const result = await createMatterContactLink(matterId, contactId, {email: emailId, phone: phoneId, address: addressId});

    if (result && result !== "error") {
      setMatterInfo({
        matterId: "",
        referenceNumber: "Auto Generate", // Let the API generate reference number
        name: "",
        createdAt: ""
      });
      setContactInfo({
        contactId: "",
        firstName: "",
        lastName: "",
        createdAt: "",
        createdBy: "",
        companyName: "",
        middleName: "",
        dateOfBirth: ""
      });
      setPhone("");
      setEmail("");
      setAddress("");
      setDesignation("");
    }

    setLoading(false);
    props.onLoading(false);
    await props.onUpdate();
    return true;
  }

  const handleEnter = async (e: React.KeyboardEvent<HTMLInputElement> ) => {
    if (e.key === "Enter") {
      await submitNewRow();
    }
  }

  const handleNext = (e: React.KeyboardEvent<HTMLInputElement> ) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
}

  const handleChange = (value: string, matterField?: keyof Matter, contactField?: keyof Contact) => {
    if (matterField) {
      const matterClone : Matter = JSON.parse(JSON.stringify(matterInfo));
      matterClone[matterField] = value;
      setMatterInfo(matterInfo => matterClone);
    } else if (contactField) {
      const contactClone : Contact = JSON.parse(JSON.stringify(contactInfo));
      contactClone[contactField] = value;
      setContactInfo(contactInfo => contactClone);
    }
  }

  return (
      <TableRow className="hover:bg-gray-50">
          <TableCell colSpan={10}>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">New Row</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Row</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-muted-foreground">Matter Number</label>
                    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={noMatter ? "border border-red-600 w-full justify-start" : "w-full justify-start"}>{selectedMatter ? selectedMatter.referenceNumber : matterInfo.referenceNumber}</Button>
                      </PopoverTrigger>
                      <PopoverPrimitive.Content
                        align="center"
                        sideOffset={4}
                        className="z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
                        onOpenAutoFocus={(e) => { e.preventDefault(); commandInputRef.current?.focus(); }}
                      >
                        <Command loop>
                          <CommandInput ref={commandInputRef} placeholder="Find or Create Matter" onChangeCapture={(e: React.ChangeEvent<HTMLInputElement>) => { handleChange(e.currentTarget.value, "referenceNumber"); } } />
                          <CommandList>
                            <CommandEmpty>Matter not found.</CommandEmpty>
                            <CommandItem
                              key={matterInfo.referenceNumber}
                              onSelect={() => {
                                setSelectedMatter(null);
                                setOpen(false);
                              }}
                            >
                              {`Create Matter Number "${matterInfo.referenceNumber}"`}
                            </CommandItem>
                            { matters?.map((matter) => (
                              <CommandItem
                                key={matter.referenceNumber}
                                value={matter.referenceNumber}
                                onSelect={(value) => {
                                  setSelectedMatter(matter);
                                  setOpen(false);
                                }}
                                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => { e.preventDefault(); }}
                              >
                                {matter.referenceNumber}
                              </CommandItem>
                            )) }
                            <CommandItem key="Auto Generate" value="Auto Generate" onSelect={() => {
                              setSelectedMatter(null);
                              setOpen(false);
                              setMatterInfo(matterInfo => ({ ...matterInfo, referenceNumber: "Auto Generate" }));
                            }}>
                              Auto Generate
                            </CommandItem>
                          </CommandList>
                        </Command>
                      </PopoverPrimitive.Content>
                    </PopoverPrimitive.Root>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-muted-foreground">Title</label>
                    <Input className={noMatter ? "border border-red-600" : ""} placeholder="Title" value={selectedMatter ? selectedMatter.name : matterInfo.name} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value,"name")} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-muted-foreground">Designation</label>
                    <Input placeholder="Designation" value={designation} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDesignation(e.currentTarget.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm text-muted-foreground">First Name</label>
                      <Input className={noContact ? "border border-red-600" : ""} placeholder="First Name" value={contactInfo.firstName} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value,undefined,"firstName")} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm text-muted-foreground">Last Name</label>
                      <Input className={noContact ? "border border-red-600" : ""} placeholder="Last Name" value={contactInfo.lastName} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value,undefined,"lastName")} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm text-muted-foreground">Middle Name</label>
                      <Input placeholder="Middle Name" value={contactInfo.middleName} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value,undefined,"middleName")} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm text-muted-foreground">Company</label>
                      <Input className={noContact ? "border border-red-600" : ""} placeholder="Company" value={contactInfo.companyName} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value,undefined,"companyName")} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-muted-foreground">Date of Birth</label>
                    <Input type="date" placeholder="Date of Birth" value={contactInfo.dateOfBirth} onKeyDown={handleNext} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value,undefined,"dateOfBirth")} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm text-muted-foreground">Phone</label>
                      <Input placeholder="Phone" value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.currentTarget.value)} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm text-muted-foreground">Email</label>
                      <Input placeholder="Email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.currentTarget.value)} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-muted-foreground">Address</label>
                    <Input placeholder="Address" value={address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.currentTarget.value)} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={async () => { const success = await submitNewRow(); if (success) setDialogOpen(false); }} disabled={loading}>
                    {loading ? "Creating..." : "Create"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TableCell>
      </TableRow>
  );
}

export default NewMatter;
